
import React, { useRef, useState, useEffect, useLayoutEffect } from "react";

import * as d3 from "d3";


const RiskMatrixSingle = ({ title, points, matrixId  }) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [lineSegments, setLineSegments] = useState([]);


  const gridRef = useRef(null);
const [bubblePx, setBubblePx] = useState(50);


  useEffect(() => {
    if (containerRef.current) {
      setContainerSize({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      });
    }
  }, []);

  const safePoints = points || [];
  ////console.log("safePoints ", safePoints)
  const getColor = (x, y) => {
    const colors = [
      ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]", "bg-[#F07167]"],
      ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]"],
      ["bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]"],
      ["bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]"],
      ["bg-[#05769D]", "bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]"]
    ];
    return colors[y][x];
  };

const calculateLineCoordinates = (pts) => {
  if (!gridRef.current) return [];

  return (pts || [])
    .map(({ x, y }) => {
      const cell = gridRef.current.querySelector(
        `#cell-${matrixId}-${4 - y}-${x}`
      );
      if (!cell) return null;

      const rect = cell.getBoundingClientRect();
      const gridRect = gridRef.current.getBoundingClientRect();

      return {
        x: rect.left + rect.width / 2 - gridRect.left,
        y: rect.top + rect.height / 2 - gridRect.top,
      };
    })
    .filter(Boolean);
};




  function calculateLineSegments(coords, radius, breakDistance) {
    const segments = [];
    const epsilon = 0.01;

    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i];
      const end = coords[i + 1];

      if (Math.abs(start.x - end.x) < epsilon && Math.abs(start.y - end.y) < epsilon) continue;

      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 2 * radius + 2 * breakDistance) {
        const unitX = dx / dist;
        const unitY = dy / dist;

        const newStart = {
          x: start.x + unitX * (radius + breakDistance),
          y: start.y + unitY * (radius + breakDistance),
        };
        const newEnd = {
          x: end.x - unitX * (radius + breakDistance),
          y: end.y - unitY * (radius + breakDistance),
        };

        segments.push([newStart, newEnd]);
      }
    }
    return segments;
  }

 
useLayoutEffect(() => {
  if (!gridRef.current) return;

  const el = gridRef.current;

  const recompute = () => {
    requestAnimationFrame(() => {
      const coords = calculateLineCoordinates(safePoints);
      const segs = calculateLineSegments(coords, 15, 10);
      setLineSegments(segs);

      const anyCell = gridRef.current?.querySelector(`#cell-${matrixId}-0-0`);
      if (anyCell) {
        const r = anyCell.getBoundingClientRect();
        const size = Math.max(18, Math.min(32, Math.floor(Math.min(r.width, r.height) * 1)));
        setBubblePx(size);
      }
    });
  };

  recompute();

  const ro = new ResizeObserver(recompute);
  ro.observe(el);

  return () => ro.disconnect();
}, [safePoints, matrixId]);


return (
 <div ref={containerRef} className="w-full h-full overflow-hidden flex flex-col">



       <style>
      {`
        @keyframes dashMove {
          to { stroke-dashoffset: -12; }
        }
      `}
    </style>
    {title && (
      <div className="text-sm font-semibold text-gray-800 px-4 pt-3 shrink-0">
        {title}
      </div>
    )}

    {/* grid area */}
    <div className="flex-1 min-h-0">
      <div ref={gridRef} className="relative w-full h-full p-4">

        <div className="grid grid-rows-5 grid-cols-5 gap-1 w-full h-full">
          {["Almost Certain", "Likely", "Possible", "Unlikely", "Rare"].map((_, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {[...Array(5)].map((_, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                 id={`cell-${matrixId}-${rowIndex}-${colIndex}`}
                  className={`relative w-full h-full flex items-center justify-center rounded-md ${getColor(colIndex, rowIndex)}`}
                  style={{ zIndex: 0 }}
                >
                  {safePoints.map((point, index) =>
                    point.x === colIndex && point.y === (4 - rowIndex) ? (
                   <div
  key={index}
  className="
    absolute flex items-center justify-center
    rounded-full bg-white border-2 border-black text-black
    transition-transform duration-150 ease-out hover:scale-110 cursor-pointer
  "
  style={{
    width: bubblePx,
    height: bubblePx,
    fontSize: bubblePx <= 22 ? 11 : 13,
    opacity: 1,
    zIndex: 2,
  }}
>
  {point.value}
</div>


                    ) : null
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        <svg
          className="absolute top-0 left-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          style={{ pointerEvents: "none", zIndex: 1 }}
        >

          <g>
            {lineSegments.map(([start, end], index) => {
              const d = `M ${start.x} ${start.y} L ${end.x} ${end.y}`;

              return (
                <React.Fragment key={`seg-${index}`}>
                  {/* base dashed line (static) */}
                  <path
                    d={d}
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                    strokeDasharray="6 6"
                    opacity={0.55}
                  />

                  {/* animated dash overlay (gives direction feeling) */}
                  <path
                    d={d}
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                    strokeDasharray="2 10"
                    style={{
                      animation: "dashMove 0.9s linear infinite",
                    }}
                    opacity={1}
                  />
                </React.Fragment>
              );
            })}
          </g>

        </svg>
      </div>
    </div>
  </div>
);

};


export default RiskMatrixSingle
